import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Container, Card, Button, Spinner } from 'react-bootstrap';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { ArrowLeft, User, MessageSquare, CheckCircle, XCircle, RotateCcw } from 'lucide-react';

// Vordefinierte Gruppen – passe diese Liste bei Bedarf an
const groupsList = [
  'Caramba',
  'Taketazu',
  'Smarties',
  'Tiguriner',
  'Yukon',
  'Atlantis',
  'Sirianer'
];

// Optimierte, memoized Komponente für einzelne Gruppen-Karten
const GroupCard = React.memo(({ summary, onGroupClick, formatDate }) => {
  return (
    <Card onClick={() => onGroupClick(summary.group, summary.active)}>
      <div className="d-flex w-100 align-items-center justify-content-between">
        <div className="card-info">
          <div className="card-title">{summary.group}</div>
          {summary.activityDate && (
            <div className="card-subtext">
              Aktivitätsdatum: {formatDate(summary.activityDate)}
            </div>
          )}
        </div>
        {(summary.anCount > 0 || summary.abCount > 0) && (
          <div className="registration-container">
            <div className="registration-green">{summary.anCount}</div>
            <div className="registration-red">{summary.abCount}</div>
          </div>
        )}
      </div>
    </Card>
  );
});

const AnAbmeldungen = () => {
  const [overallSummary, setOverallSummary] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [detailRegistrations, setDetailRegistrations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [animating, setAnimating] = useState(false);

  // Übersichtsdaten pro Gruppe laden – mittels useCallback memoisiert
  const fetchSummary = useCallback(async () => {
    setLoading(true);
    const promises = groupsList.map(async (groupName) => {
      try {
        const q = query(
          collection(db, 'chastlizettel'),
          where('selectedGroups', 'array-contains', groupName),
          orderBy('timestamp', 'desc'),
          limit(1)
        );
        const querySnapshot = await getDocs(q);
        let anCount = 0,
          abCount = 0,
          activityDate = null,
          active = false;
        if (!querySnapshot.empty) {
          const docSnap = querySnapshot.docs[0];
          const data = docSnap.data();
          const now = new Date();
          // Nur wenn ein verabschiedungsTimestamp vorhanden ist, weniger als 6h vergangen sind und nicht im Planungsmodus
          if (
            data.verabschiedungsTimestamp &&
            (now - data.verabschiedungsTimestamp.toDate()) < 6 * 3600 * 1000 &&
            data.activityType !== 'planung'
          ) {
            active = true;
            activityDate = data.startDatum || data.besammlungsDatum;
            const regsRef = collection(db, 'chastlizettel', docSnap.id, 'registrations');
            const qRegs = query(regsRef, orderBy('timestamp', 'desc'));
            const regsSnap = await getDocs(qRegs);
            const regs = [];
            regsSnap.forEach((r) => regs.push(r.data()));
            anCount = regs.filter(r => r.action === 'anmelden').length;
            abCount = regs.filter(r => r.action === 'abmelden').length;
          }
        }
        return { group: groupName, anCount, abCount, activityDate, active };
      } catch (error) {
        console.error(`Fehler beim Laden für Gruppe ${groupName}:`, error);
        return { group: groupName, anCount: 0, abCount: 0, activityDate: null, active: false };
      }
    });
    const results = await Promise.all(promises);
    setOverallSummary(results);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  // Detailregistrierungen laden – ebenfalls mit useCallback memoisiert
  const loadDetailRegistrations = useCallback(async (groupName) => {
    setLoading(true);
    try {
      const q = query(
        collection(db, 'chastlizettel'),
        where('selectedGroups', 'array-contains', groupName),
        orderBy('timestamp', 'desc'),
        limit(1)
      );
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const docSnap = querySnapshot.docs[0];
        const data = docSnap.data();
        const now = new Date();
        if (
          data.verabschiedungsTimestamp &&
          (now - data.verabschiedungsTimestamp.toDate()) < 6 * 3600 * 1000 &&
          data.activityType !== 'planung'
        ) {
          const regsRef = collection(db, 'chastlizettel', docSnap.id, 'registrations');
          const qRegs = query(regsRef, orderBy('timestamp', 'desc'));
          const regsSnap = await getDocs(qRegs);
          const regs = [];
          regsSnap.forEach((r) => regs.push({ id: r.id, ...r.data() }));
          setDetailRegistrations(regs);
        } else {
          setDetailRegistrations([]);
        }
      } else {
        setDetailRegistrations([]);
      }
    } catch (error) {
      console.error(`Fehler beim Laden der Detailregistrierungen für ${groupName}:`, error);
    }
    setLoading(false);
  }, []);

  // Gruppen-Klick: Detailansicht öffnen
  const handleGroupClick = useCallback((groupName, active) => {
    setSelectedGroup(groupName);
    if (active) {
      loadDetailRegistrations(groupName);
    } else {
      setDetailRegistrations([]);
    }
  }, [loadDetailRegistrations]);

  // Hilfsfunktion: Formatierung des Datums (Tag.Monat.JJJJ)
  const formatDate = useCallback((timestamp) => {
    let date;
    if (timestamp && typeof timestamp === 'object' && 'seconds' in timestamp) {
      date = new Date(timestamp.seconds * 1000);
    } else {
      date = new Date(timestamp);
    }
    if (isNaN(date.getTime())) return "";
    return date.toLocaleDateString('de-CH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }, []);

  // Verwende useMemo, um die Filterung der Detailregistrierungen zu optimieren
  const anmeldungen = useMemo(() => detailRegistrations.filter(reg => reg.action === 'anmelden'), [detailRegistrations]);
  const abmeldungen = useMemo(() => detailRegistrations.filter(reg => reg.action === 'abmelden'), [detailRegistrations]);

  // Übersichtsdaten zur ausgewählten Gruppe
  const selectedGroupData = useMemo(() => overallSummary.find(s => s.group === selectedGroup), [overallSummary, selectedGroup]);

  // Optimierter Reload-Handler: Nur die benötigten Daten neu laden
  const handleReloadClick = async () => {
    setAnimating(true);
    await fetchSummary();
    if (selectedGroup) {
      await loadDetailRegistrations(selectedGroup);
    }
    setAnimating(false);
  };

  return (
    <Container fluid className="anabmeldungen p-0">
      {/* Für optimale Performance sollten Inline-Styles in eine separate CSS-Datei ausgelagert werden */}
      <style>{`
        html, body, .anabmeldungen {
          height: 100%;
        }
        .anabmeldungen {
          background-color: rgb(243, 242, 241);
          min-height: 100vh;
          padding-bottom: 2rem;
        }
        .anabmeldungen .content-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 1rem;
        }
        /* Header aus LinkCollections */
        .mobile-header, .desktop-header {
          background-color: #F3F2F1;
          border-bottom: 1px solid rgba(0,0,0,0.1);
          padding: 1rem;
        }
        .mobile-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .mobile-header h2 {
          font-weight: bold;
          margin: 0;
          font-size: 2rem;
          color: #333;
          text-align: left;
        }
        .reload-button {
          border: none;
          background: transparent;
          cursor: pointer;
        }
        /* Dreh-Animation nur einmal beim Klick */
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin {
          animation: spin 0.5s linear;
        }
        .desktop-header h2 {
          color: #333;
          font-weight: 600;
          font-size: 2.4rem;
          text-align: center;
          margin-bottom: 1rem;
        }
        /* Restlicher Seiteninhalt */
        .groups-grid {
          display: grid;
          gap: 1rem;
          padding: 1rem 0;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
        }
        @media (max-width: 767px) {
          .groups-grid {
            grid-template-columns: 1fr;
          }
        }
        /* Glassmorphism Look für die Gruppen-Cards */
        .card {
          cursor: pointer;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0.75rem 1rem;
          height: 80px;
          background: rgba(255, 255, 255, 0.3);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.3);
          border-radius: 8px;
        }
        .card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
        }
        .card-info {
          display: flex;
          flex-direction: column;
          justify-content: center;
        }
        .card-title {
          margin: 0;
          font-size: 1.2rem;
          font-weight: bold;
        }
        .card-subtext {
          font-size: 0.85rem;
          color: #666;
        }
        .registration-container {
          display: flex;
          gap: 0.5rem;
          align-items: center;
        }
        .registration-green, .registration-red {
          width: 50px;
          height: 50px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.1rem;
          color: #fff;
          border-radius: 4px;
        }
        .registration-green {
          background-color: #28a745;
        }
        .registration-red {
          background-color: #dc3545;
        }
        /* Detailansicht */
        .detail-header {
          margin-top: 0.5rem;
          margin-bottom: 1rem;
        }
        .detail-header h3 {
          font-size: 1.8rem;
          margin-bottom: 0.5rem;
          display: flex;
          align-items: baseline;
        }
        .detail-header h3 span {
          font-size: 1rem;
          color: #777;
          margin-left: 0.5rem;
        }
        hr {
          border: 0;
          border-top: 1px solid #ddd;
          margin: 0.5rem 0 1rem;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 1rem;
        }
        th, td {
          border-bottom: 1px solid #eee;
          padding: 0.75rem 0.5rem;
          vertical-align: middle;
        }
        th {
          background-color: #f8f9fa;
          text-align: left;
          font-weight: 600;
        }
        td {
          font-size: 0.95rem;
        }
        /* Angepasste Spaltenbreiten: Person 55%, Kommentar 45% */
        table th:nth-child(1), table td:nth-child(1) {
          width: 55%;
        }
        table th:nth-child(2), table td:nth-child(2) {
          width: 45%;
        }
        .icon {
          vertical-align: middle;
          margin-right: 0.25rem;
        }
      `}</style>

      <div className="content-container">
        {/* Mobile Header */}
        <div className="mobile-header d-md-none">
          <h2>An/Abmeldungen</h2>
          <button className="reload-button" onClick={handleReloadClick}>
            <RotateCcw size={24} className={animating ? "spin" : ""} />
          </button>
        </div>

        {/* Desktop Header */}
        <div className="desktop-header d-none d-md-block">
          <h2>An/Abmeldungen</h2>
        </div>

        {/* Restlicher Seiteninhalt */}
        {loading ? (
          <div className="py-5 text-center">
            <Spinner animation="border" role="status" variant="primary">
              <span className="visually-hidden">Lädt...</span>
            </Spinner>
          </div>
        ) : (
          <>
            {!selectedGroup ? (
              <div className="groups-grid">
                {overallSummary.map(summary => (
                  <GroupCard
                    key={summary.group}
                    summary={summary}
                    onGroupClick={handleGroupClick}
                    formatDate={formatDate}
                  />
                ))}
              </div>
            ) : (
              // Detailansicht
              <div className="px-3 mt-2">
                <Button variant="link" className="text-muted p-0" onClick={() => setSelectedGroup('')}>
                  <ArrowLeft size={16} className="icon" /> Zurück
                </Button>
                <div className="detail-header">
                  <h3>
                    <strong>{selectedGroup}</strong>
                    {selectedGroupData && selectedGroupData.activityDate && (
                      <span>{formatDate(selectedGroupData.activityDate)}</span>
                    )}
                  </h3>
                  <hr />
                </div>

                {/* Anmeldungen */}
                <div className="mt-3">
                  <div className="d-flex align-items-center mb-2">
                    <CheckCircle size={20} color="#28a745" className="icon" />
                    <h5 className="mb-0">
                      Anmeldungen <span className="badge bg-success ms-2">{anmeldungen.length}</span>
                    </h5>
                  </div>
                  {anmeldungen.length > 0 ? (
                    <table>
                      <thead>
                        <tr>
                          <th>Person</th>
                          <th>Kommentar</th>
                        </tr>
                      </thead>
                      <tbody>
                        {anmeldungen.map((reg, idx) => (
                          <tr key={idx}>
                            <td>
                              <User size={16} className="icon" />
                              {reg.firstName} {reg.lastName}
                              {reg.pfadiname && <span> v/o {reg.pfadiname}</span>}
                            </td>
                            <td>
                              <MessageSquare size={16} className="icon" />
                              {reg.comment || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="text-muted">Keine Anmeldungen vorhanden.</p>
                  )}
                </div>
                <hr />

                {/* Abmeldungen */}
                <div className="mt-3">
                  <div className="d-flex align-items-center mb-2">
                    <XCircle size={20} color="#dc3545" className="icon" />
                    <h5 className="mb-0">
                      Abmeldungen <span className="badge bg-danger ms-2">{abmeldungen.length}</span>
                    </h5>
                  </div>
                  {abmeldungen.length > 0 ? (
                    <table>
                      <thead>
                        <tr>
                          <th>Person</th>
                          <th>Kommentar</th>
                        </tr>
                      </thead>
                      <tbody>
                        {abmeldungen.map((reg, idx) => (
                          <tr key={idx}>
                            <td>
                              <User size={16} className="icon" />
                              {reg.firstName} {reg.lastName}
                              {reg.pfadiname && <span> v/o {reg.pfadiname}</span>}
                            </td>
                            <td>
                              <MessageSquare size={16} className="icon" />
                              {reg.comment || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="text-muted">Keine Abmeldungen vorhanden.</p>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Container>
  );
};

export default AnAbmeldungen;